import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
  getRecommendations,
  refreshRecommendations,
  updateRecommendationStatus,
} from '../services/recommendationsApi';
import type { RecommendationStatus } from '../types/recommendations';

const QUERY_KEY = 'recommendations';

export function useRecommendations(tenantId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEY, tenantId],
    queryFn: () => getRecommendations(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshRecommendations(tenantId!),
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY, tenantId], data);
      message.success('Diagnóstico atualizado com sucesso!');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ recId, status }: { recId: number; status: RecommendationStatus }) =>
      updateRecommendationStatus(tenantId!, recId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, tenantId] });
    },
    onError: (error: unknown) => {
      // A 404 means the recommendation ID no longer exists in the backend
      // (likely deleted by a refresh that regenerated pending recs with new IDs).
      // Force a refetch so the UI drops stale cards, and show a friendly message.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, tenantId] });
        message.info('Esta recomendação foi atualizada. Carregando a lista mais recente...');
      } else {
        message.error('Não foi possível atualizar a recomendação. Tente novamente.');
      }
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    /**
     * RE-LER o diagnostico, sem regerar nada.
     *
     * DISTINTO de `refresh` (logo abaixo), que e' uma MUTACAO: ela roda o
     * diagnostico de novo no backend e sobrescreve o cache. Oferecer `refresh`
     * como "tentar de novo" depois de uma leitura que falhou trocaria um
     * problema NOSSO (rede) por um recomputo do lado do cliente.
     */
    refetch: query.refetch,
    refresh: refreshMutation.mutate,
    isRefreshing: refreshMutation.isPending,
    updateStatus: updateStatusMutation.mutate,
    isUpdating: updateStatusMutation.isPending,
  };
}
